import { Box, type BoxProps, Circle, Spinner, Wrap, WrapItem } from '@chakra-ui/react'
import { faChevronLeft, faChevronRight, faClose } from '@fortawesome/pro-regular-svg-icons'
import { faMagnifyingGlassMinus, faMagnifyingGlassPlus } from '@fortawesome/pro-solid-svg-icons'
import Image from 'next/legacy/image'
import { useCallback, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
// eslint-disable-next-line import/no-unresolved
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
// eslint-disable-next-line import/extensions, import/no-unresolved
import 'yet-another-react-lightbox/styles.css'

import getImageLoader from '@/utils/getImageLoader'
import logger from '@/utils/logger'

import Icon from '@/components/UI/Icon/Icon'

import { FILE_UPLOAD_CLASS_NAME } from './constants'
import type { FileUpload } from './types'

function UploadGalleryItem({
  upload,
  onExpand,
  bgColor,
  disableImageClick = false,
}: {
  upload: FileUpload
  onExpand: () => void
  bgColor?: BoxProps['bg']
  disableImageClick?: boolean
}): JSX.Element {
  const { src, loading = false } = upload

  const onImageError = useCallback(() => {
    logger.error('CloudFlare image loader error', { src })
  }, [src])

  const imageLoader = getImageLoader({ size: 200 })

  return (
    <Box
      cursor={loading || disableImageClick ? 'auto' : 'zoom-in'}
      onClick={() => {
        if (loading || disableImageClick) {
          return
        }
        onExpand()
      }}
      className={FILE_UPLOAD_CLASS_NAME}
      position="relative"
      width="100px"
      height="100px"
      borderRadius="8px"
      border="1px solid"
      borderColor="border.emphasized"
      bgColor={bgColor}
    >
      {loading ? (
        <Circle bg="bg" p="2px" position="absolute" top="-10px" right="-10px">
          <Spinner
            thickness="2px"
            speed="0.65s"
            emptyColor="bg.emphasized"
            color="primary.solid"
            size="sm"
          />
        </Circle>
      ) : null}
      <Image
        layout="fill"
        alt="Uploaded image"
        src={src}
        loader={loading ? undefined : imageLoader}
        objectFit="contain"
        style={{ borderRadius: '8px' }}
        onError={onImageError}
      />
    </Box>
  )
}

export default function FileUploadGallery({
  uploads,
  disableImageClick = false,
  bgColor,
}: {
  uploads: FileUpload[]
  disableImageClick?: boolean
  bgColor?: BoxProps['bg']
}): JSX.Element | null {
  const [indexToExpand, setIndexToExpand] = useState<number | null>(null)

  if (uploads.length === 0) {
    return null
  }

  return (
    <Wrap spacing="16px">
      {disableImageClick ? null : (
        <Lightbox
          open={indexToExpand != null}
          close={() => setIndexToExpand(null)}
          index={indexToExpand ?? 0}
          slides={uploads.map(({ src }) => ({ src }))}
          plugins={[Zoom]}
          animation={{ fade: 330, swipe: 500, navigation: 0 }}
          carousel={{ finite: true }}
          zoom={{ maxZoomPixelRatio: 4, doubleClickMaxStops: 3 }}
          render={{
            iconZoomIn: () => <Icon size="xl" faIcon={faMagnifyingGlassPlus} />,
            iconZoomOut: () => <Icon size="xl" faIcon={faMagnifyingGlassMinus} />,
            iconClose: () => <Icon size="xl" faIcon={faClose} />,
            iconPrev: () => <Icon size="xl" faIcon={faChevronLeft} />,
            iconNext: () => <Icon size="xl" faIcon={faChevronRight} />,
          }}
        />
      )}
      {uploads.map((upload, index) => (
        <WrapItem key={upload.src}>
          <UploadGalleryItem
            upload={upload}
            disableImageClick={disableImageClick}
            bgColor={bgColor}
            onExpand={() => {
              setIndexToExpand(index)
            }}
          />
        </WrapItem>
      ))}
    </Wrap>
  )
}
